import { useCallback, useEffect, useState } from 'react';
import Cookies from 'universal-cookie';
import { resetWishlistCache } from './useWishlist';

const AUTH_EVENT = 'ij:auth-change';

/**
 * Notify every mounted component that the stored session changed.
 * `storage` events only fire in *other* tabs, so login/logout in this tab
 * needs an explicit signal — previously the header only refreshed because
 * an unrelated parent re-render happened to run its effect again.
 */
export const notifyAuthChange = () => {
  // Wishlist membership belongs to the previous session; drop it so the
  // next read refetches for whoever is signed in now.
  resetWishlistCache();
  window.dispatchEvent(new Event(AUTH_EVENT));
};

/**
 * Update the cached display name — e.g. after the "edit profile" form saves
 * a new one — and let every mounted `useAuth()` (the header included) pick
 * it up immediately. This reuses the same event `notifyAuthChange` fires,
 * but skips its wishlist-cache reset: the signed-in user hasn't changed,
 * only their name, so there's nothing there to invalidate.
 */
export const setUserName = (name) => {
  localStorage.setItem('user_name', name || '');
  window.dispatchEvent(new Event(AUTH_EVENT));
};

export const clearSession = () => {
  const cookies = new Cookies();
  localStorage.removeItem('token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_name');
  localStorage.removeItem('common_id');
  cookies.remove('user_id');
  cookies.remove('user_id', { path: '/admin' });
  notifyAuthChange();
};

const readSession = () => ({
  isLoggedIn: Boolean(localStorage.getItem('token')),
  userName: localStorage.getItem('user_name') || '',
});

/** Current session state, kept in sync across tabs and modals. */
const useAuth = () => {
  const [session, setSession] = useState(readSession);

  const refresh = useCallback(() => setSession(readSession()), []);

  useEffect(() => {
    window.addEventListener('storage', refresh);
    window.addEventListener(AUTH_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener(AUTH_EVENT, refresh);
    };
  }, [refresh]);

  return { ...session, refresh };
};

export default useAuth;
